import React, { useRef } from 'react';
import { ModalToggleState, SizingToolState } from '../models/actionStates';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { assetPathFnState, pageContextState } from '../recoil/atoms';
import { useSetModalState } from '../hooks/actions/useModalState';
import supportModalStyles from './SupportModal.module.scss';
import { isMobileState, sizingToolState } from '../recoil/selectors';
import { SelectionType } from '../enums/SelectionType.enum';
import { PageContext } from '../enums/PageContext.enum';
import axios from 'axios';
import { Spinner } from '../root/components/Spinner';
import classNames from 'classnames';
import useSafariBfCacheInvalidator from '../hooks/useSafariBfCacheInvalidator';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import styles from '../modal_singleton/Modal.module.scss';
import { useProductSelection } from '../hooks/useProductSelection';
import { Icon } from '../icons/Icon';
interface ZendeskArticle {
  body: string;
  name: string;
}

declare const window: HoneyloveWindow;

export const SupportModal: React.FC = () => {
  const assetPathFn = useRecoilValue(assetPathFnState);
  const setModal = useSetModalState();
  const [supportInputText, setSupportInputText] = React.useState<string>();
  const [answers, setAnswers] = React.useState<ZendeskArticle[]>([]);
  const isMobile = useRecoilValue(isMobileState);
  const setSizingToolState = useSetRecoilState(sizingToolState);
  const pageContext = useRecoilValue(pageContextState);
  const [answer, setAnswer] = React.useState<ZendeskArticle>();
  const [noResults, setNoResults] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  useSafariBfCacheInvalidator(setIsLoading);
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const { product: selectedProduct } = useProductSelection(SelectionType.ProductSection);

  const openSizingTool = () => {
    setSizingToolState({
      sizingToolState: SizingToolState.Open,
      selectionType: SelectionType.ProductSection,
      selectionKey: '0',
      product: pageContext === PageContext.ProductPage ? selectedProduct : undefined,
    });
  };

  const fetchZendeskContent = async () => {
    setIsLoading(true);
    setAnswer(undefined);
    setNoResults(false);
    const results = await axios.get(`https://honeylovehelp.zendesk.com/api/v2/help_center/articles/search?query=${supportInputText}`);

    setIsLoading(false);
    setAnswers(results?.data?.results);
    setNoResults(results?.data?.results.length === 0);
  };

  const handleKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      fetchZendeskContent();
    }
  };

  const viewAnswer = (selectedAnswer: ZendeskArticle) => {
    setAnswer(selectedAnswer);
    modalWrapperRef.current?.scrollTo(0, 0);
  };

  const openZendeskWidget = () => {
    window?.zE('webWidget', 'show');
    window?.zE('webWidget', 'updateSettings', { webWidget: { helpCenter: { suppress: true } } });
    window?.zE('webWidget', 'open');
  };

  return (
    <React.Fragment>
      <div className={styles.modalHeader}>
        <button
          onClick={() => setAnswer(undefined)}
          className={classNames(styles.modalControl, supportModalStyles.modalControl)}
          style={answer ? { padding: '5px 0 3px 6px' } : { opacity: 0 }}
        >
          <Icon name="chevron-left" style={{ width: '12px' }} />
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setModal({ toggleState: ModalToggleState.Closed })}
          className={classNames(styles.modalControl, supportModalStyles.modalControl)}
          style={{ padding: '6px 0px 0px 5px' }}
        >
          <Icon name="close" style={{ width: '12px' }} />
        </button>
      </div>
      <div
        ref={modalWrapperRef}
        className={classNames(styles.modalBody, supportModalStyles.modalBody)}
        style={{
          backgroundImage: 'none',
        }}
      >
        {answer && (
          <span className={styles.modalTitle} style={{ display: 'inline-block', marginTop: 30, paddingBottom: 0, fontSize: '22pt', lineHeight: 1.2 }}>
            {answer?.name}
          </span>
        )}
        {!answer && <span className={styles.modalTitle}>How can we help you?</span>}
        {!answer && (
          <div className={supportModalStyles.inputContainer}>
            <input
              onKeyDown={handleKeyPress}
              onChange={(e) => setSupportInputText(e.target.value)}
              className={supportModalStyles.askInput}
              placeholder={'Ask us anything'}
            />
            {isMobile ? (
              <button className={supportModalStyles.getHelpMobile} onClick={fetchZendeskContent}>
                <img src={assetPathFn('icon-arrow-right-01.svg')} alt="submit" />
              </button>
            ) : (
              <button className={supportModalStyles.getHelp} onClick={fetchZendeskContent}>
                Get Help
              </button>
            )}
          </div>
        )}
        {answers.length === 0 && (
          <div className={supportModalStyles.emptyResults}>
            {isLoading && <Spinner styleOverrides={{ spinner: { marginTop: 15 } }} visible={true} />}
            {noResults && <span>No results found.</span>}
          </div>
        )}
        {!answer && (
          <div className={supportModalStyles.answerWrapper}>
            {answers.slice(0, 5).map((answerPreview, i) => {
              return (
                <div key={i} className={supportModalStyles.answerContainer}>
                  <button className={supportModalStyles.answerTitle} onClick={() => viewAnswer(answerPreview)}>
                    <h2>
                      {answerPreview.name} <img src={assetPathFn('sizing-tool-chevron-back.png')} alt="back" />
                    </h2>
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {answer && (
          <div
            className={supportModalStyles.answerHtml}
            dangerouslySetInnerHTML={{
              __html: answer.body,
            }}
          />
        )}
        <span className={styles.modalTitle}>Quick links</span>
        <div className={supportModalStyles.ctaWrapper}>
          <button onClick={openZendeskWidget} className={supportModalStyles.ctaCard}>
            <div>Contact us</div>
            <div>Speak with a rep</div>
            <div className={supportModalStyles.ctaIcon}>
              <img src={assetPathFn('icon-new-window.svg')} alt="contact us" />
            </div>
          </button>
          <button className={supportModalStyles.ctaCard} onClick={openSizingTool}>
            <div>Find your fit</div>
            <div>Launch our fit finder</div>
            <div className={supportModalStyles.ctaIcon}>
              <img src={assetPathFn('icon-new-window.svg')} alt="find your fit" />
            </div>
          </button>

          <a href="/pages/track-your-order" className={supportModalStyles.ctaCard}>
            <div>Track order</div>
            <div>View order status page</div>
            <div className={supportModalStyles.ctaIcon}>
              <img src={assetPathFn('icon-new-window.svg')} alt="track order" />
            </div>
          </a>

          <a href="/pages/returns" className={supportModalStyles.ctaCard}>
            <div>Start a return</div>
            <div>Visit our returns center</div>
            <div className={supportModalStyles.ctaIcon}>
              <img src={assetPathFn('icon-new-window.svg')} alt="start return" />
            </div>
          </a>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SupportModal;
